// colormaps.js
export const colormaps = {
  speechStable: {
    name: 'Speech Stable',
    description: 'Stable colors for 41-86dB range',
    getColor: (intensityValue) => {
      // intensityValue is 0-90 from mapping, representing 41-86dB
      if (intensityValue < 20) { // 41-50dB
        return `rgb(0, 100, 200)`;     // Dark blue
      } else if (intensityValue < 35) { // 50-60dB
        return `rgb(0, 200, 200)`;     // Cyan
      } else if (intensityValue < 50) { // 60-70dB
        return `rgb(0, 200, 0)`;       // Green
      } else if (intensityValue < 65) { // 70-75dB
        return `rgb(200, 200, 0)`;     // Yellow
      } else if (intensityValue < 80) { // 75-80dB
        return `rgb(255, 150, 0)`;     // Orange
      } else {                         // 80-86dB
        return `rgb(255, 0, 0)`;       // Red
      }
    }
  },

  stableBands: {
    name: 'Stable Bands',
    description: 'Clear bands for 41-86dB range',
    getColor: (intensityValue) => {
      if (intensityValue < 15) return `rgb(70, 70, 180)`;    // 41-50dB: Purple-blue
      else if (intensityValue < 30) return `rgb(0, 150, 220)`; // 50-60dB: Blue
      else if (intensityValue < 45) return `rgb(0, 200, 150)`; // 60-70dB: Teal
      else if (intensityValue < 60) return `rgb(50, 220, 50)`; // 70-75dB: Green
      else if (intensityValue < 75) return `rgb(220, 220, 0)`; // 75-80dB: Yellow
      else return `rgb(220, 100, 0)`;                         // 80-86dB: Orange-red
    }
  },

  intensitySteps: {
    name: 'Intensity Steps',
    description: 'Strong steps in speech range',
    getColor: (intensityValue) => {
      if (intensityValue < 10) return `rgb(80, 80, 160)`;    // 41-48dB
      else if (intensityValue < 20) return `rgb(0, 120, 200)`; // 48-55dB
      else if (intensityValue < 30) return `rgb(0, 180, 180)`; // 55-62dB
      else if (intensityValue < 40) return `rgb(0, 200, 100)`; // 62-69dB
      else if (intensityValue < 50) return `rgb(100, 220, 0)`; // 69-73dB
      else if (intensityValue < 60) return `rgb(200, 220, 0)`; // 73-77dB
      else if (intensityValue < 70) return `rgb(255, 180, 0)`; // 77-81dB
      else return `rgb(255, 80, 0)`;                         // 81-86dB
    }
  },

  vibrantFire: {
    name: 'Vibrant Fire',
    description: 'Strong red-orange-yellow progression',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.3) {
        // 0-27dB: Deep blue to purple
        const t = normalized / 0.3;
        const r = Math.round(30 + (80 * t));
        const g = Math.round(0);
        const b = Math.round(120 + (80 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.6) {
        // 27-54dB: Purple to bright red - STRONG CONTRAST
        const t = (normalized - 0.3) / 0.3;
        const r = Math.round(110 + (145 * t));
        const g = Math.round(0);
        const b = Math.round(200 - (200 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        // 54-90dB: Red to bright yellow - MAXIMUM CONTRAST
        const t = (normalized - 0.6) / 0.4;
        const r = 255;
        const g = Math.round(0 + (255 * t));
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  electricOcean: {
    name: 'Electric Ocean',
    description: 'High contrast blue-green-yellow',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.25) {
        // 0-22.5dB: Dark blue
        const t = normalized / 0.25;
        const r = Math.round(0);
        const g = Math.round(0 + (100 * t));
        const b = Math.round(80 + (120 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.5) {
        // 22.5-45dB: Blue to electric blue
        const t = (normalized - 0.25) / 0.25;
        const r = Math.round(0);
        const g = Math.round(100 + (100 * t));
        const b = Math.round(200);
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.75) {
        // 45-67.5dB: Electric blue to bright green - STRONG CONTRAST
        const t = (normalized - 0.5) / 0.25;
        const r = Math.round(0);
        const g = Math.round(200);
        const b = Math.round(200 - (150 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        // 67.5-90dB: Green to bright yellow - MAXIMUM CONTRAST
        const t = (normalized - 0.75) / 0.25;
        const r = Math.round(0 + (255 * t));
        const g = 200;
        const b = Math.round(50 - (50 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  neonSpectrum: {
    name: 'Neon Spectrum',
    description: 'Vibrant neon colors with clear steps',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.2) {
        // 0-18dB: Deep purple
        const t = normalized / 0.2;
        const r = Math.round(80 + (40 * t));
        const g = Math.round(0);
        const b = Math.round(120 + (60 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.4) {
        // 18-36dB: Purple to pink
        const t = (normalized - 0.2) / 0.2;
        const r = Math.round(120 + (100 * t));
        const g = Math.round(0);
        const b = Math.round(180 - (80 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.6) {
        // 36-54dB: Pink to red - CLEAR STEP
        const t = (normalized - 0.4) / 0.2;
        const r = Math.round(220 + (35 * t));
        const g = Math.round(0 + (80 * t));
        const b = Math.round(100 - (100 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.8) {
        // 54-72dB: Red to orange - STRONG STEP
        const t = (normalized - 0.6) / 0.2;
        const r = 255;
        const g = Math.round(80 + (100 * t));
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        // 72-90dB: Orange to neon yellow - MAXIMUM STEP
        const t = (normalized - 0.8) / 0.2;
        const r = 255;
        const g = Math.round(180 + (75 * t));
        const b = Math.round(0 + (100 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  contrastHeat: {
    name: 'Contrast Heat',
    description: 'High contrast heat map style',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.33) {
        // 0-30dB: Black to dark red
        const t = normalized / 0.33;
        const r = Math.round(50 + (150 * t));
        const g = Math.round(0);
        const b = Math.round(0);
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.66) {
        // 30-60dB: Dark red to bright red - BIG JUMP
        const t = (normalized - 0.33) / 0.33;
        const r = Math.round(200 + (55 * t));
        const g = Math.round(0 + (100 * t));
        const b = Math.round(0);
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        // 60-90dB: Bright red to white - MAXIMUM JUMP
        const t = (normalized - 0.66) / 0.34;
        const r = 255;
        const g = Math.round(100 + (155 * t));
        const b = Math.round(0 + (255 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  rainbowBold: {
    name: 'Rainbow Bold',
    description: 'Strong rainbow with distinct color bands',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.2) {
        // 0-18dB: Deep blue
        const t = normalized / 0.2;
        const r = Math.round(0);
        const g = Math.round(0);
        const b = Math.round(100 + (100 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.4) {
        // 18-36dB: Blue to cyan - CLEAR CHANGE
        const t = (normalized - 0.2) / 0.2;
        const r = Math.round(0);
        const g = Math.round(0 + (200 * t));
        const b = 200;
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.6) {
        // 36-54dB: Cyan to green - STRONG CHANGE
        const t = (normalized - 0.4) / 0.2;
        const r = Math.round(0);
        const g = 200;
        const b = Math.round(200 - (200 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.8) {
        // 54-72dB: Green to yellow - VERY STRONG
        const t = (normalized - 0.6) / 0.2;
        const r = Math.round(0 + (255 * t));
        const g = 200;
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        // 72-90dB: Yellow to red - MAXIMUM
        const t = (normalized - 0.8) / 0.2;
        const r = 255;
        const g = Math.round(200 - (200 * t));
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  plasma: {
    name: 'Plasma',
    description: 'High contrast, perceptually uniform',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.25) {
        const t = normalized / 0.25;
        const r = Math.round(13 + (135 * t));
        const g = Math.round(8 + (28 * t));
        const b = Math.round(135 + (95 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.5) {
        const t = (normalized - 0.25) / 0.25;
        const r = Math.round(148 + (107 * t));
        const g = Math.round(36 + (87 * t));
        const b = Math.round(230 - (30 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.75) {
        const t = (normalized - 0.5) / 0.25;
        const r = Math.round(255);
        const g = Math.round(123 + (132 * t));
        const b = Math.round(200 - (200 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.75) / 0.25;
        const r = 255;
        const g = Math.round(255);
        const b = Math.round(0 + (55 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  turbo: {
    name: 'Turbo',
    description: 'Google Turbo - high contrast',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      const r = Math.round(35.874 + (322.8 * normalized) - (499.6 * normalized * normalized) + (306.4 * normalized * normalized * normalized));
      const g = Math.round(11.75 + (322.8 * normalized) - (58.8 * normalized * normalized) - (193.2 * normalized * normalized * normalized));
      const b = Math.round(241.9 - (312.8 * normalized) + (243.6 * normalized * normalized) - (71.4 * normalized * normalized * normalized));
      
      return `rgb(${Math.max(0, Math.min(255, r))}, ${Math.max(0, Math.min(255, g))}, ${Math.max(0, Math.min(255, b))})`;
    }
  },

  viridis: {
    name: 'Viridis',
    description: 'Perceptually uniform, colorblind friendly',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.25) {
        const t = normalized / 0.25;
        const r = Math.round(68 * t);
        const g = Math.round(1 + (53 * t));
        const b = Math.round(84 + (77 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.5) {
        const t = (normalized - 0.25) / 0.25;
        const r = Math.round(68 + (63 * t));
        const g = Math.round(54 + (122 * t));
        const b = Math.round(161 - (17 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.75) {
        const t = (normalized - 0.5) / 0.25;
        const r = Math.round(131 + (123 * t));
        const g = Math.round(176 - (26 * t));
        const b = Math.round(144 - (84 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.75) / 0.25;
        const r = Math.round(254);
        const g = Math.round(150 + (105 * t));
        const b = Math.round(60 + (20 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  coolwarm: {
    name: 'Cool-Warm',
    description: 'Intuitive: blue (low) to red (high)',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.5) {
        const t = normalized / 0.5;
        const r = Math.round(59 * t);
        const g = Math.round(76 + (86 * t));
        const b = Math.round(192 + (63 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.5) / 0.5;
        const r = Math.round(180 + (75 * t));
        const g = Math.round(4 + (76 * t));
        const b = Math.round(38 + (32 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  hot: {
    name: 'Hot',
    description: 'Black-red-yellow-white, good for speech',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.375) {
        const t = normalized / 0.375;
        const r = Math.round(255 * t);
        return `rgb(${r}, 0, 0)`;
      } else if (normalized < 0.75) {
        const t = (normalized - 0.375) / 0.375;
        const r = 255;
        const g = Math.round(255 * t);
        return `rgb(${r}, ${g}, 0)`;
      } else {
        const t = (normalized - 0.75) / 0.25;
        const r = 255;
        const g = 255;
        const b = Math.round(255 * t);
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  jet: {
    name: 'Jet',
    description: 'High contrast, traditional scientific',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.125) {
        const t = normalized / 0.125;
        const r = 0;
        const g = 0;
        const b = Math.round(128 + (127 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.375) {
        const t = (normalized - 0.125) / 0.25;
        const r = 0;
        const g = Math.round(0 + (255 * t));
        const b = 255;
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.625) {
        const t = (normalized - 0.375) / 0.25;
        const r = Math.round(0 + (255 * t));
        const g = 255;
        const b = Math.round(255 - (255 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.875) {
        const t = (normalized - 0.625) / 0.25;
        const r = 255;
        const g = Math.round(255 - (255 * t));
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.875) / 0.125;
        const r = Math.round(255 - (127 * t));
        const g = 0;
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  rainbow: {
    name: 'Rainbow',
    description: 'Classic rainbow spectrum',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.2) {
        const t = normalized / 0.2;
        const r = Math.round(128 - (78 * t));
        const g = Math.round(0);
        const b = Math.round(128 + (127 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.4) {
        const t = (normalized - 0.2) / 0.2;
        const r = Math.round(50);
        const g = Math.round(0 + (255 * t));
        const b = Math.round(255);
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.6) {
        const t = (normalized - 0.4) / 0.2;
        const r = Math.round(50 - (50 * t));
        const g = Math.round(255);
        const b = Math.round(255 - (255 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.8) {
        const t = (normalized - 0.6) / 0.2;
        const r = Math.round(0 + (255 * t));
        const g = Math.round(255);
        const b = Math.round(0);
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.8) / 0.2;
        const r = Math.round(255);
        const g = Math.round(255 - (255 * t));
        const b = Math.round(0);
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  inferno: {
    name: 'Inferno',
    description: 'Perceptually uniform, fire-like',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.25) {
        const t = normalized / 0.25;
        const r = Math.round(0);
        const g = Math.round(0);
        const b = Math.round(4 + (60 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.5) {
        const t = (normalized - 0.25) / 0.25;
        const r = Math.round(60 + (68 * t));
        const g = Math.round(0 + (4 * t));
        const b = Math.round(64 + (127 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.75) {
        const t = (normalized - 0.5) / 0.25;
        const r = Math.round(128 + (126 * t));
        const g = Math.round(4 + (80 * t));
        const b = Math.round(191 - (64 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.75) / 0.25;
        const r = Math.round(254);
        const g = Math.round(84 + (170 * t));
        const b = Math.round(127 - (127 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  },

  magma: {
    name: 'Magma',
    description: 'Perceptually uniform, purple to yellow',
    getColor: (intensityValue) => {
      const normalized = Math.max(0, Math.min(1, intensityValue / 90));
      
      if (normalized < 0.25) {
        const t = normalized / 0.25;
        const r = Math.round(0);
        const g = Math.round(0);
        const b = Math.round(4 + (60 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.5) {
        const t = (normalized - 0.25) / 0.25;
        const r = Math.round(60 + (67 * t));
        const g = Math.round(0 + (16 * t));
        const b = Math.round(64 + (131 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else if (normalized < 0.75) {
        const t = (normalized - 0.5) / 0.25;
        const r = Math.round(127 + (127 * t));
        const g = Math.round(16 + (107 * t));
        const b = Math.round(195 - (67 * t));
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        const t = (normalized - 0.75) / 0.25;
        const r = Math.round(254);
        const g = Math.round(123 + (132 * t));
        const b = Math.round(128 - (128 * t));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
  }
};

export const defaultColormap = 'speechStable';